import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { supabase } from "@/lib/supabase";
import { useSession } from "@/hooks/use-session";
import { EmptyState, SkeletonLoader } from "@/components/system";
import { formatShortDate } from "@/lib/date-format";

/**
 * /projects (SPEC 3.4) — read-only navigation surface.
 *
 * BW20.3g (Bug 10): grouped layout so order is predictable.
 *
 * Group 1: Pinned active / planned, sorted by end_date asc nulls last
 * Group 2: Unpinned active / planned, sorted by end_date asc nulls last
 * Group 3: Evergreen, sorted alphabetically by name
 * Group 4: Completed / archived / abandoned, hidden behind a toggle
 *
 * Section headers only render when the group has rows.
 */

type ProjectRow = {
  id: string;
  name: string;
  slug: string;
  intent: string;
  kind: string;
  end_date: string | null;
  status: string;
  priority: string;
  pinned: boolean;
  next_up: string | null;
  open_count: number;
  tags: string[];
  updated_at: string;
};

function daysUntil(end: string | null): number | null {
  if (!end) return null;
  const target = new Date(`${end}T00:00:00`);
  const now = new Date();
  now.setHours(0, 0, 0, 0);
  return Math.round((target.getTime() - now.getTime()) / 86_400_000);
}

const ACTIVE_STATUSES = new Set(["active", "planned"]);
const DONE_STATUSES = new Set(["completed", "cancelled", "archived", "abandoned"]);

type ProjectGroup = {
  key: "pinned" | "active" | "evergreen" | "done";
  label: string;
  rows: ProjectRow[];
};

function groupProjects(rows: ProjectRow[]): ProjectGroup[] {
  const pinned: ProjectRow[] = [];
  const active: ProjectRow[] = [];
  const evergreen: ProjectRow[] = [];
  const done: ProjectRow[] = [];

  for (const r of rows) {
    if (DONE_STATUSES.has(r.status)) {
      done.push(r);
      continue;
    }
    if (r.kind === "evergreen") {
      evergreen.push(r);
      continue;
    }
    if (ACTIVE_STATUSES.has(r.status) && r.pinned) {
      pinned.push(r);
      continue;
    }
    if (ACTIVE_STATUSES.has(r.status)) {
      active.push(r);
      continue;
    }
    // 'paused' or other in-flight non-active rows: bucket with active so
    // they remain visible without an extra header.
    active.push(r);
  }

  const byEndAsc = (a: ProjectRow, b: ProjectRow): number => {
    const ad = a.end_date ? Date.parse(`${a.end_date}T00:00:00`) : Number.POSITIVE_INFINITY;
    const bd = b.end_date ? Date.parse(`${b.end_date}T00:00:00`) : Number.POSITIVE_INFINITY;
    if (ad !== bd) return ad - bd;
    return Date.parse(b.updated_at) - Date.parse(a.updated_at);
  };
  const byNameAsc = (a: ProjectRow, b: ProjectRow): number => a.name.localeCompare(b.name);

  pinned.sort(byEndAsc);
  active.sort(byEndAsc);
  evergreen.sort(byNameAsc);
  done.sort((a, b) => {
    const ad = a.end_date ? Date.parse(`${a.end_date}T00:00:00`) : 0;
    const bd = b.end_date ? Date.parse(`${b.end_date}T00:00:00`) : 0;
    return bd - ad; // newest finished first
  });

  return [
    { key: "pinned", label: "Pinned", rows: pinned },
    { key: "active", label: "Active", rows: active },
    { key: "evergreen", label: "Evergreen", rows: evergreen },
    { key: "done", label: `Completed (${done.length})`, rows: done },
  ];
}

const LS_KEY = "projects.show_completed";

function readShowCompleted(): boolean {
  try {
    return localStorage.getItem(LS_KEY) === "true";
  } catch {
    return false;
  }
}

export function Projects() {
  const { session } = useSession();
  const userId = session?.user?.id;
  const [showCompleted, setShowCompleted] = useState(readShowCompleted);

  function toggleCompleted(next: boolean) {
    try {
      localStorage.setItem(LS_KEY, String(next));
    } catch {}
    setShowCompleted(next);
  }

  const q = useQuery<ProjectRow[]>({
    queryKey: ["projects_list", userId],
    enabled: Boolean(userId),
    staleTime: 60_000,
    queryFn: async () => {
      if (!userId) return [];
      const { data, error } = await supabase
        .from("project")
        .select(
          "id, name, slug, intent, kind, end_date, status, priority, pinned, next_up, open_count, tags, updated_at",
        )
        .eq("user_id", userId);
      if (error) throw error;
      return (data ?? []) as ProjectRow[];
    },
  });

  return (
    <article aria-labelledby="projects-heading">
      <p
        style={{
          fontFamily: "var(--font-sans)",
          fontSize: "var(--type-micro)",
          letterSpacing: "0.06em",
          textTransform: "uppercase",
          color: "var(--fg-3)",
          margin: 0,
        }}
      >
        Projects
      </p>
      <h1
        id="projects-heading"
        style={{
          fontFamily: "var(--font-serif)",
          fontStyle: "italic",
          fontWeight: 500,
          fontSize: "var(--type-display-m)",
          margin: "var(--space-2) 0 var(--space-6)",
        }}
      >
        Open campaigns.
      </h1>

      {q.isLoading ? (
        <SkeletonLoader variant="card" count={3} height={120} />
      ) : !q.data || q.data.length === 0 ? (
        <EmptyState
          headline="No projects yet."
          sub="Run the seed loader from Settings → Data, or wait for onboarding to populate them."
        />
      ) : (
        <ProjectsGrouped
          rows={q.data}
          showCompleted={showCompleted}
          onToggleCompleted={toggleCompleted}
        />
      )}
    </article>
  );
}

function ProjectsGrouped({
  rows,
  showCompleted,
  onToggleCompleted,
}: {
  rows: ProjectRow[];
  showCompleted: boolean;
  onToggleCompleted: (next: boolean) => void;
}) {
  const groups = groupProjects(rows);
  const visible = groups.filter((g) =>
    g.key === "done" ? showCompleted && g.rows.length > 0 : g.rows.length > 0,
  );
  const hasDone = groups.find((g) => g.key === "done")?.rows.length ?? 0;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-6)" }}>
      <style>{`@media (max-width: 767px) { .haus-projects-grid { grid-template-columns: 1fr !important; } }`}</style>
      {visible.map((g) => (
        <section key={g.key} aria-labelledby={`projects-group-${g.key}`}>
          <h2
            id={`projects-group-${g.key}`}
            style={{
              fontSize: "var(--type-micro)",
              letterSpacing: "0.06em",
              textTransform: "uppercase",
              color: "var(--fg-3)",
              margin: "0 0 var(--space-3)",
              fontWeight: 500,
            }}
          >
            {g.label}
          </h2>
          <ul
            style={{
              listStyle: "none",
              margin: 0,
              padding: 0,
              display: "grid",
              gap: "var(--space-3)",
              gridTemplateColumns: "repeat(2, 1fr)",
            }}
            className="haus-projects-grid"
          >
            {g.rows.map((p) => (
              <li key={p.id}>
                <ProjectCard project={p} />
              </li>
            ))}
          </ul>
        </section>
      ))}
      {hasDone > 0 && (
        <button
          type="button"
          onClick={() => onToggleCompleted(!showCompleted)}
          style={{
            alignSelf: "flex-start",
            background: "transparent",
            border: "1px solid var(--hair-2)",
            color: "var(--fg-2)",
            borderRadius: "var(--radius-chip)",
            padding: "6px 12px",
            fontSize: "var(--type-meta)",
            cursor: "pointer",
          }}
        >
          {showCompleted ? "Hide" : "Show"} completed ({hasDone})
        </button>
      )}
    </div>
  );
}

function ProjectCard({ project }: { project: ProjectRow }) {
  const days = daysUntil(project.end_date);
  const hot = days != null && days >= 0 && days <= 14;
  return (
    <Link
      to={`/projects/${project.id}`}
      style={{
        display: "flex",
        flexDirection: "column",
        gap: "var(--space-2)",
        padding: "var(--space-5)",
        background: "var(--bg-2)",
        border: `1px solid ${hot ? "var(--accent-dim)" : "var(--hair)"}`,
        borderRadius: "var(--radius-card)",
        textDecoration: "none",
        color: "var(--fg)",
        minHeight: 120,
      }}
    >
      <header style={{ display: "flex", justifyContent: "space-between", gap: "var(--space-2)" }}>
        <span
          style={{
            fontSize: "var(--type-micro)",
            letterSpacing: "0.06em",
            textTransform: "uppercase",
            color: project.pinned ? "var(--accent)" : "var(--fg-3)",
          }}
        >
          {project.kind === "time_bounded" ? "Time-bounded" : "Evergreen"}
          {project.pinned && " · Pinned"}
        </span>
        {days != null && (
          <span
            className="mono"
            style={{
              fontSize: "var(--type-meta)",
              color: hot ? "var(--accent)" : "var(--fg-3)",
            }}
          >
            T{days >= 0 ? "−" : "+"}
            {Math.abs(days)}d
          </span>
        )}
      </header>
      <p
        style={{
          margin: 0,
          fontFamily: "var(--font-serif)",
          fontStyle: "italic",
          fontWeight: 500,
          fontSize: "var(--type-body-l)",
          color: "var(--fg)",
        }}
      >
        {project.name}
      </p>
      <p
        style={{
          margin: 0,
          fontSize: "var(--type-body-s)",
          color: "var(--fg-2)",
          lineHeight: 1.4,
        }}
      >
        {project.intent || "No intent set"}
      </p>
      <footer
        style={{
          display: "flex",
          justifyContent: "space-between",
          gap: "var(--space-2)",
          marginTop: "auto",
          fontSize: "var(--type-meta)",
          color: "var(--fg-3)",
        }}
      >
        <span>{project.next_up ? `Next: ${project.next_up}` : project.status}</span>
        <span className="mono">
          {project.end_date ? formatShortDate(project.end_date) : ""}
        </span>
      </footer>
    </Link>
  );
}
